import React, { useState, useRef, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { CSpinner } from "@coreui/react-pro";
import { useNavigate, useParams } from "react-router-dom";
import { editShort, viewShort } from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { CTooltip } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilInfo } from "@coreui/icons";

const EditShort = () => {
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [initialValues, setInitialValues] = useState({
    title: "",
    description: "",
    video: null,
    status: "",
    id: null,
  });
  const [shortUrl, setShortUrl] = useState("");

  const fetchData = async (id) => {
    setIsLoading(true);
    try {
      const response = await viewShort({ id: id });
      const fetchedValues = {
        title: response.data.title || "",
        description: response.data.description || "",
        video: response.data.file_name || "",
        status: response.data.status || "",
        id: id || null,
      };
      setInitialValues(fetchedValues);
      setShortUrl(
        `${process.env.REACT_APP_API_URL}/uploads/${fetchedValues.video}`
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string().nullable(),
    description: Yup.string().nullable(),
    status: Yup.string()
      .nullable()
      .notOneOf([""], "Please select a valid status"),
    video: Yup.mixed()
      .required("Video is required")
      .test(
        "fileFormat",
        "Unsupported video format. Only MP4 or MOV allowed.",
        (value) => {
          if (!value) return true;
          if (value instanceof File) {
            return ["video/mp4", "video/quicktime"].includes(value.type);
          }
          return true;
        }
      )
      .test("fileSize", "File size must not exceed 2MB.", (value) => {
        if (!value) return true;
        if (value instanceof File) {
          return value.size <= 2 * 1024 * 1024;
        }
        return true;
      }),
  });

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, setFieldTouched }
  ) => {
    setIsSubmittingForm(true);
    try {
      const formData = new FormData();
      formData.append("id", id);

      // Always include title, description, and status in the payload
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("status", values.status);

      if (values.video && values.video instanceof File) {
        formData.append("video", values.video);
      }

      // Check if any changes were made
      const hasChanges = 
        values.title !== initialValues.title ||
        values.description !== initialValues.description ||
        values.status !== initialValues.status ||
        (values.video && values.video instanceof File);

      if (!hasChanges) {
        toast.info("No changes detected.");
        setIsSubmittingForm(false);
        setSubmitting(false);
        navigate("/shorts");
        return;
      }

      const response = await editShort(formData);
      if (response?.data) {
        toast.success("Short updated successfully!");
        navigate("/shorts");
      } else {
        throw new Error("Failed to update short.");
      }
    } catch (error) {
      console.error("Failed to edit short:", error);
      toast.error(error.message || "Failed to edit short. Please try again.");
      setErrors({ submit: "Failed to edit short", ...error.fieldErrors });
      Object.keys(values).forEach((field) =>
        setFieldTouched(field, true, false)
      );
    } finally {
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  useEffect(() => {
    fetchData(id);
  }, [id]);

  const handleBackClick = () => {
    const currentPath = window.location.pathname;
    if (currentPath.includes(`/details/${id}/edit`)) {
      navigate(`/shorts/details/${id}`);
    } else {
      navigate("/shorts");
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="card border-light">
          <div className="card-body p-4">
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Skeleton
                height={40}
                width={100}
                style={{ marginRight: "10px" }}
              />
              <Skeleton height={40} width={100} />
            </div>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ setFieldValue, isSubmitting, errors, touched }) => (
            <Form>
              {isSubmittingForm && (
                <div className="form-overlay">
                  <CSpinner color="primary" />
                </div>
              )}
              <div
                className="card border-light"
                style={{ display: "flex", flexDirection: "row" }}
              >
                <div className="card-body p-4" style={{ flex: 1 }}>
                  {initialValues.video?.endsWith(".mov") ? (
                    <video
                      controls
                      style={{ aspectRatio: "9:16", height: "100%", border: "2px dashed #808080", padding:"5px", minHeight:"500px" }}
                      src={shortUrl}
                      title={initialValues.title || "N/A"}
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <iframe
                      className=""
                      style={{ aspectRatio: "9:16", height: "100%", border: "2px dashed #808080", padding:"5px", minHeight:"500px" }}
                      src={shortUrl}
                      title={initialValues.title || "N/A"}
                    ></iframe>
                  )}
                </div>

                <div
                  className="card-body p-4"
                  style={{
                    flex: 4,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  {errors.submit && (
                    <div className="alert alert-danger">{errors.submit}</div>
                  )}
                  {/* Form fields */}
                  <div className="row">
                    {/* Title */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="title" className="form-label">
                        {/* <span className="text-danger">*</span>  */}
                        Title
                      </label>{" "}
                      <Field
                        name="title"
                        as="input"
                        type="text"
                        placeholder="Enter Title"
                        className={`form-control ${
                          touched.title && errors.title ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="title"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    {/* Description */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="description" className="form-label">
                        {/* <span className="text-danger">*</span>  */}
                        Description
                      </label>{" "}
                      <Field
                        name="description"
                        as="input"
                        type="text"
                        placeholder="Enter Description"
                        className={`form-control ${
                          touched.description && errors.description
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="description"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                  <div className="row">
                    {/* Video */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="video" className="form-label">
                        <span className="text-danger">*</span> Upload Video
                      </label>
                      <CTooltip
                        content={
                          <>
                            <ul>
                              <li>Max file size: 2MB</li>
                              <li>Supported formats: MP4, MOV</li>
                              <li>
                                Recommended dimension: <br />
                                1920 (h) x 1080 (w)
                              </li>
                            </ul>
                          </>
                        }
                        placement="right"
                      >
                        <CIcon
                          icon={cilInfo}
                          className="mx-2 text-danger"
                        ></CIcon>
                      </CTooltip>
                      {initialValues.video && (
                        <a
                          href={shortUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-link p-0"
                        >
                          <span className="badge bg-secondary">
                            Existing file:{" "}
                            {initialValues.video
                              .split("/uploads/")
                              .pop()
                              .substring(0, 8) + "..."}
                          </span>
                        </a>
                      )}
                      <input
                        id="video"
                        name="video"
                        type="file"
                        ref={fileInputRef}
                        className={`form-control ${
                          touched.video && errors.video ? "is-invalid" : ""
                        }`}
                        accept="video/mp4,video/quicktime"
                        onChange={(event) => {
                          const video = event.currentTarget.files[0];
                          setFieldValue("video", video);
                        }}
                      />
                      <ErrorMessage
                        name="video"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    {/* Status */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="status" className="form-label">
                        {/* <span className="text-danger">*</span>  */}
                        Status
                      </label>{" "}
                      <div className="input-group">
                        <Field
                          name="status"
                          as="select"
                          className={`form-select ${
                            touched.status && errors.status ? "is-invalid" : ""
                          }`}
                        >
                          <option value="">Select a status</option>
                          <option key="Pending" value="Pending">
                            Pending Review
                          </option>
                          <option key="Approved" value="Approved">
                            Approved
                          </option>
                          <option key="Rejected" value="Rejected">
                            Rejected
                          </option>
                        </Field>
                        <ErrorMessage
                          name="status"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                  </div>
                  <div className=" d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn me-3 ansa-button-secondary"
                      onClick={handleBackClick}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="btn ansa-button-primary"
                      disabled={isSubmitting}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default EditShort;
